import './QuotationTable.scss';
import { Table, Card, Empty, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import InputComponent from '../../common/InputComponent/InputComponent';
import SelectComponent from '../../common/SelectComponent/SelectComponent';
import { useDispatch, useSelector } from 'react-redux';
import ButtonIconSolidLarge from '../../common/ButtonIconSolid/ButtonIconSolidLarge';
import { Icon } from '../../icon/icon';
import ButtonSecondaryMedium from '../../common/ButtonSecondary/ButtonSecondaryMedium';
import ButtonIconGhostMedium from '../../common/ButtonIconGhost/ButtonIconGhostMedium';
import { fetchProductPricing } from '../../../store/actions/quotation.action';
import { convertForeignCurrency } from '../../../utils/currency/currency.utils';

const { Column, ColumnGroup, Summary } = Table;

const QuotationTable = ({ data = [], setData, form, supplierSelectRef }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    productNames,
    suppliers,
    materialTypes,
    materialDescription,
    unit,
    groups,
  } = useSelector(state => state.quotation);

  return (
    <Card
      className="quotation-table bg-fills-white border-none rounded-lg md:px-40 md:py-24 p-16"
      classNames={{ body: '!p-0' }}
    >
      <Table
        dataSource={data}
        rowKey="id"
        locale={{
          emptyText: (
            <Empty
              description={t(
                'quotation.table.noData',
                'Hãy bắt đầu thêm sản phẩm vào bảng báo giá của bạn'
              )}
              className="p-16"
            />
          ),
        }}
        scroll={{ x: 1000 }}
        bordered
        pagination={false}
        summary={pageData => {
          const total = pageData.reduce((sum, record) => {
            const recordTotal = Number(record.total ?? 0);
            return sum + (isNaN(recordTotal) ? 0 : recordTotal);
          }, 0);
          return (
            <Summary.Row>
              <Summary.Cell
                index={0}
                colSpan={1}
                align="left"
                className="text-title-small-emphasized bg-fills-primary-varient"
              >
                {t('quotation.table.total', 'Tổng cộng')}
              </Summary.Cell>
              <Summary.Cell index={1} colSpan={12} align="left">
                <span className="font-medium">
                  {convertForeignCurrency(total)}
                </span>
              </Summary.Cell>
            </Summary.Row>
          );
        }}
      >
        <Column
          title={t('quotation.table.productName', 'Tên Sản Phẩm')}
          dataIndex={'product_name'}
          key={'product_name'}
          width={180}
          minWidth={180}
          align={'center'}
          render={(_, record) => (
            <div className="flex px-12 py-8">
              <Form.Item
                name={[record.key, 'product_name']}
                className="w-[154px]"
              >
                <SelectComponent
                  options={[
                    ...(productNames?.[record.key]?.data?.map(item => ({
                      label: item,
                      value: item,
                      className:
                        'max-w-[300px] md:max-w-[600px] lg:max-w-full [&_.ant-select-item-option-content]:whitespace-normal',
                    })) ?? []),
                  ]}
                  showSearch
                  disabled={productNames?.[record.key]?.isLoading}
                  placeholder={t(
                    'quotation.table.selectProduct',
                    'Chọn sản phẩm'
                  )}
                  allowClear={true}
                  className="w-full"
                />
              </Form.Item>
            </div>
          )}
        />
        <Column
          title={t('quotation.table.group', 'Nhóm')}
          dataIndex={'group'}
          key={'group'}
          width={180}
          minWidth={180}
          align={'center'}
          render={(_, record) => (
            <div className="flex px-12 py-8">
              <Form.Item name={[record.key, 'group']} className="w-[154px]">
                <SelectComponent
                  options={[
                    ...(groups?.[record.key]?.data?.map(item => ({
                      label: item,
                      value: item,
                      className:
                        'max-w-[300px] md:max-w-[600px] lg:max-w-full [&_.ant-select-item-option-content]:whitespace-normal',
                    })) ?? []),
                  ]}
                  disabled={groups?.[record.key]?.isLoading}
                  showSearch
                  placeholder={t('quotation.table.selectGroup', 'Chọn nhóm')}
                  allowClear={true}
                  className="w-full"
                />
              </Form.Item>
            </div>
          )}
        />
        <Column
          title={t('quotation.table.materialType', 'Loại ván')}
          dataIndex={'material_type'}
          key={'material_type'}
          width={180}
          minWidth={180}
          align={'center'}
          render={(_, record) => (
            <div className="flex px-12 py-8">
              <Form.Item
                name={[record.key, 'material_type']}
                className="w-[154px]"
              >
                <SelectComponent
                  options={[
                    ...(materialTypes?.[record.key]?.data?.map(item => ({
                      label: item,
                      value: item,
                      className:
                        'max-w-[300px] md:max-w-[600px] lg:max-w-full [&_.ant-select-item-option-content]:whitespace-normal',
                    })) ?? []),
                  ]}
                  disabled={materialTypes?.[record.key]?.isLoading}
                  showSearch
                  placeholder={t(
                    'quotation.table.selectMaterial',
                    'Chọn loại ván'
                  )}
                  allowClear={true}
                  className="w-full"
                />
              </Form.Item>
            </div>
          )}
        />
        <Column
          title={t('quotation.table.material_description', 'Mô Tả Chất liệu')}
          dataIndex={'material_description'}
          key={'material_description'}
          width={180}
          minWidth={180}
          align={'center'}
          render={(_, record) => (
            <div className="flex px-12 py-8">
              <Form.Item
                name={[record.key, 'material_description']}
                className="w-[154px]"
              >
                <SelectComponent
                  options={[
                    ...(materialDescription?.[record.key]?.data?.map(item => ({
                      label: item,
                      value: item,
                      className:
                        'max-w-[300px] md:max-w-[600px] lg:max-w-full [&_.ant-select-item-option-content]:whitespace-normal',
                    })) ?? []),
                  ]}
                  showSearch
                  disabled={materialDescription?.[record.key]?.isLoading}
                  placeholder={t(
                    'quotation.table.selectDescription',
                    'Chọn mô tả chất liệu'
                  )}
                  allowClear={true}
                  className="w-full"
                />
              </Form.Item>
            </div>
          )}
        />
        <Column
          title={t('quotation.table.unit', 'Đơn Vị Tính')}
          dataIndex={'unit'}
          key={'unit'}
          width={120}
          minWidth={120}
          align={'center'}
          render={(_, record) => (
            <div className="flex px-12 py-8">
              <Form.Item name={[record.key, 'unit']} className="w-[104px]">
                <SelectComponent
                  options={[
                    ...(unit?.[record.key]?.data?.map(item => ({
                      label: item,
                      value: item,
                      className:
                        'max-w-[300px] md:max-w-[600px] lg:max-w-full [&_.ant-select-item-option-content]:whitespace-normal',
                    })) ?? []),
                  ]}
                  showSearch
                  disabled={unit?.[record.key]?.isLoading}
                  placeholder={t(
                    'quotation.table.selectUnit',
                    'Chọn đơn vị tính'
                  )}
                  allowClear={true}
                  className="w-full"
                />
              </Form.Item>
            </div>
          )}
        />
        <ColumnGroup
          title={t('quotation.table.size', 'Kích thước(mm)')}
          dataIndex={'size'}
          key={'size'}
          width={300}
          minWidth={300}
          align={'center'}
        >
          <Column
            title={t('quotation.table.length', 'Dài')}
            dataIndex={['size', 'length']}
            key={['size', 'length']}
            width={100}
            align={'center'}
            render={(text, record) => {
              if (text === '-') {
                return (
                  <div className="flex px-12 py-8">
                    <Form.Item
                      name={[record.key, 'size', 'length']}
                      className="w-[84px]"
                    >
                      <InputComponent
                        type="number"
                        placeholder={t(
                          'quotation.table.enterLength',
                          'Nhập chiều dài'
                        )}
                      />
                    </Form.Item>
                  </div>
                );
              }
              return <span className="font-medium">{text}</span>;
            }}
          />
          <Column
            title={t('quotation.table.height', 'Cao')}
            dataIndex={['size', 'height']}
            key={['size', 'height']}
            width={100}
            align={'center'}
            render={(text, record) => {
              if (text === '-') {
                return (
                  <div className="flex px-12 py-8">
                    <Form.Item
                      name={[record.key, 'size', 'height']}
                      className="w-[84px]"
                    >
                      <InputComponent
                        type="number"
                        placeholder={t(
                          'quotation.table.enterHeight',
                          'Nhập chiều cao'
                        )}
                      />
                    </Form.Item>
                  </div>
                );
              }
              return <span className="font-medium">{text}</span>;
            }}
          />
          <Column
            title={t('quotation.table.depth', 'Sâu')}
            dataIndex={['size', 'depth']}
            key={['size', 'depth']}
            width={100}
            align={'center'}
            render={(text, record) => {
              if (text === '-') {
                return (
                  <div className="flex px-12 py-8">
                    <Form.Item
                      name={[record.key, 'size', 'depth']}
                      className="w-[84px]"
                    >
                      <InputComponent
                        type="number"
                        placeholder={t(
                          'quotation.table.enterDepth',
                          'Nhập chiều sâu'
                        )}
                      />
                    </Form.Item>
                  </div>
                );
              }
              return <span className="font-medium">{text}</span>;
            }}
          />
        </ColumnGroup>
        <Column
          title={t(
            'quotation.table.quantity_formula',
            'Cách Tính Khối Lượng Hạng Mục'
          )}
          dataIndex={'quantity_formula'}
          key={'quantity_formula'}
          width={200}
          minWidth={200}
          align={'center'}
          render={text => <span className="font-medium">{text}</span>}
        />
        <Column
          title={t('quotation.table.price', 'Đơn Giá')}
          dataIndex={'price'}
          key={'price'}
          width={100}
          align={'center'}
          render={text =>
            text && (
              <span className="font-medium">
                {(() => {
                  const num = Number(text);
                  if (isNaN(num)) {
                    return text;
                  }
                  return convertForeignCurrency(Number(text));
                })()}
              </span>
            )
          }
        />
        <Column
          title={t('quotation.table.quantity', 'Khối Lượng')}
          dataIndex={'quantity'}
          key={'quantity'}
          width={100}
          minWidth={100}
          align={'center'}
          render={(_, record) => {
            if (record.quantity_formula !== '-') {
              return (
                <span className="font-medium">{record.quantity ?? '-'}</span>
              );
            } else {
              return (
                <div className="flex px-12 py-8">
                  <Form.Item
                    name={[record.key, 'quantity']}
                    className="w-[84px]"
                  >
                    <InputComponent
                      type="number"
                      placeholder={t(
                        'quotation.table.enterQuantity',
                        'Nhập số lượng'
                      )}
                    />
                  </Form.Item>
                </div>
              );
            }
          }}
        />
        <Column
          title={t('quotation.table.price', 'Thành tiền')}
          dataIndex={'total'}
          key={'total'}
          width={100}
          minWidth={100}
          align={'center'}
          render={text =>
            text && (
              <span className="font-medium">
                {convertForeignCurrency(Number(text))}
              </span>
            )
          }
        />

        {/* <Column
          title={t('quotation.table.image', 'Hình ảnh tham khảo')}
          dataIndex={'image'}
          key={'image'}
          width={200}
          align={'center'}
          render={text => <span className="font-medium">{text}</span>}
        /> */}
        <Column
          title={t('quotation.table.description', 'Ghi chú')}
          dataIndex={'description'}
          key={'description'}
          align={'center'}
          render={text => <span className="font-medium">{text}</span>}
        />
        <Column
          dataIndex={'extra'}
          key={'extra'}
          width={50}
          minWidth={50}
          align={'center'}
          render={(_, record) => (
            <div className="flex p-8">
              <ButtonIconGhostMedium
                icon={<Icon.ICClose />}
                onClick={() => {
                  setData(prev => prev.filter(item => item.key !== record.key));

                  form.setFieldsValue({
                    [record.key]: {
                      productName: undefined,
                      materialType: undefined,
                      material_description: undefined,
                    },
                  });
                }}
              />
            </div>
          )}
        />
      </Table>
      <div className="flex self-stretch items-center justify-center gap-16 py-32">
        <ButtonSecondaryMedium
          title={t('quotation.table.addProduct', 'Thêm Sản Phẩm')}
          icon={<Icon.ICAdd />}
          className="w-fit"
          onClick={() => {
            if (form.getFieldValue('supplier')) {
              setData(prev => {
                const lastItem = prev[prev.length - 1]?.key ?? 0;
                dispatch(
                  fetchProductPricing({
                    key: lastItem + 1,
                    params: { supplier: form.getFieldValue('supplier') },
                  })
                );
                return [...prev, { key: lastItem + 1 }];
              });
            } else {
              supplierSelectRef.current?.focus();
            }
          }}
        />
      </div>
    </Card>
  );
};

export default QuotationTable;
